// src/pages/Analyse.tsx
import * as React from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  TextField,
  Typography,
  Divider,
  List,
  ListItem,
  Paper,
} from '@mui/material';
import MuiAccordion, { AccordionProps as MuiAccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import AppTheme from '../theme/AppTheme';
import ColorModeSelect from '../theme/ColorModeSelect';
import ProfileDropdown from '../components/ProfileDropdown';
import { auth, db } from '../firebase/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

/* ----------------- Styled Components ----------------- */

// Main page container with a radial gradient background
const AnalysisContainer = styled(Stack)(({ theme }) => ({
  height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
  minHeight: '100%',
  padding: theme.spacing(3),
  position: 'relative',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    // Light mode background
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 95%), hsl(0, 0%, 98%))',
    backgroundRepeat: 'no-repeat',
    // Dark mode background
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

/* ----------------- Custom Accordion Styling ----------------- */
interface AccordionProps extends MuiAccordionProps {
  children: NonNullable<React.ReactNode>;
}

const StyledAccordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square={false} {...props} />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.04)'
      : 'rgba(0, 0, 0, 0.04)',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
  '&:before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    minHeight: 'auto',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
    '&.Mui-expanded': {
      minHeight: 'auto',
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      margin: theme.spacing(1, 0),
    },
  },
  '& .MuiAccordionDetails-root': {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  },
}));

const StyledAccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={<ExpandMoreIcon />} {...props} />
))(({ theme }) => ({
  padding: theme.spacing(0, 2),
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: theme.palette.text.secondary,
  },
}));

const StyledAccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
}));

/* ----------------- Types & Interfaces ----------------- */
// Define the TypeScript interface for the cargo analysis result
interface CargoAnalysisData {
  tons: number;
  from: string;
  to: string;
  analysis: {
    mode: string;
    price: string;
    day: string;
  }[];
}

// Define the response schema that the Gemini API is expected to return
const cargoResponseSchema = {
  type: 'OBJECT',
  properties: {
    tons: { type: 'NUMBER' },
    from: { type: 'STRING' },
    to: { type: 'STRING' },
    analysis: {
      type: 'ARRAY',
      items: {
        type: 'OBJECT',
        properties: {
          mode: { type: 'STRING' },
          price: { type: 'STRING' },
          day: { type: 'STRING' },
        },
        required: ['mode', 'price', 'day'],
      },
    },
  },
  required: ['tons', 'from', 'to', 'analysis'],
};

/* ----------------- CargoAnalysisResults Component ----------------- */
const CargoAnalysisResults: React.FC<{ data: CargoAnalysisData }> = ({ data }) => {
  const navigate = useNavigate();
  const [selectedOptionIndex, setSelectedOptionIndex] = React.useState(0);

  const handleProceedToBilling = () => {
    // Create the cargo data object to pass to the billing page
    const cargoData = {
      tons: data.tons,
      from: data.from,
      to: data.to,
      selectedOption: data.analysis[selectedOptionIndex]
    };

    // Navigate to billing page with the selected data
    navigate('/billing', { state: { cargoData } });
  };

  // Define all possible transport modes
  const allTransportModes = ['sea', 'air', 'rail', 'road'];
  
  
  const availableModes = new Map();
  const validAnalysisItems = data.analysis.filter(item => {
    const isValid = 
      item.price && 
      item.day && 
      item.price.toLowerCase() !== 'n/a' && 
      item.day.toLowerCase() !== 'n/a';
    
    if (isValid) {
      availableModes.set(item.mode.toLowerCase(), item);
    }
    return isValid;
  });

  return (
    <Box
      sx={{
        mt: 3,
        p: 3,
        borderRadius: 2,
        boxShadow: (theme) =>
          theme.palette.mode === 'dark'
            ? '0 2px 5px rgba(0,0,0,0.4)'
            : '0 2px 5px rgba(0,0,0,0.1)',
        backgroundColor: (theme) => theme.palette.background.paper,
      }}
    >
      <Typography variant="h6" sx={{ mb: 1 }}>
        Cargo Transport Analysis
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Transporting {data.tons} ton(s) from <strong>{data.from}</strong> to{' '}
        <strong>{data.to}</strong>.
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Available Modes of Travel:
      </Typography>
      <List>
        {/* Render only valid analysis items */}
        {validAnalysisItems.map((item, idx) => (
          <ListItem
            key={idx}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              borderBottom:
                idx !== validAnalysisItems.length - 1
                  ? (theme) => `1px solid ${theme.palette.divider}`
                  : 'none',
              py: 1,
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {item.mode.toUpperCase()}
              </Typography>
              <Typography variant="body2">
                Price: {item.price} | Delivery Day: {item.day}
              </Typography>
            </Box>
            <Button
              variant={selectedOptionIndex === idx ? 'contained' : 'outlined'}
              color="primary"
              size="small"
              sx={{ mt: { xs: 1, sm: 0 } }}
              onClick={() => setSelectedOptionIndex(idx)}
            >
              {selectedOptionIndex === idx ? 'Selected' : 'Select'}
            </Button>
          </ListItem>
        ))}
        
        {/* Then render any missing or invalid transport modes as disabled options */}
        {allTransportModes.map((mode) => {
          // Skip if this mode is already rendered as available
          if (availableModes.has(mode)) return null;
          
          // Check if this mode exists in the original analysis but with N/A values
          const originalItem = data.analysis.find(
            item => item.mode.toLowerCase() === mode.toLowerCase()
          );
          
          return (
            <ListItem
              key={`unavailable-${mode}`}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                py: 1,
                opacity: 0.6,
              }}
            >
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {mode.toUpperCase()}
                </Typography>
                <Typography variant="body2" color="error">
                  {originalItem 
                    ? "Not available for this cargo weight/route" 
                    : "Not available for this route"}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                disabled
                sx={{ mt: { xs: 1, sm: 0 } }}
              >
                Not Available
              </Button>
            </ListItem>
          );
        })}
      </List>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleProceedToBilling}
          disabled={validAnalysisItems.length === 0}
        >
          {validAnalysisItems.length === 0 
            ? "No Available Transport Options" 
            : "Proceed to Billing"}
        </Button>
      </Box>
    </Box>
  );
};

/* ----------------- Main Page Component ----------------- */
const AnalysePage: React.FC = () => {
  // State variables for cargo management inputs
  const [tons, setTons] = React.useState<number | ''>('');
  const [fromLocation, setFromLocation] = React.useState('');
  const [toLocation, setToLocation] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState('');
  const [cargoData, setCargoData] = React.useState<CargoAnalysisData | null>(null);

  const handleAnalyse = async () => {
    // Validate inputs: ensure all required fields are provided.
    if (!tons || !fromLocation || !toLocation) return;
  
    // Clear previous results and set loading state.
    setResult('');
    setCargoData(null);
    setLoading(true);
  
    try {
      const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
      if (!apiKey) throw new Error('API key is missing');
  
      // Build the prompt for cargo transport analysis.
      // The prompt instructs the API to consider sea, air, rail, and road options.
      const prompt = `Provide a structured cargo transport analysis for a shipment of ${tons} ton(s) from ${fromLocation} to ${toLocation}. Consider the following transportation modes: sea, air, rail, and road. For each mode, provide realistic estimates for price and delivery days in the following JSON format:
  {
    "tons": <number>,
    "from": "<origin>",
    "to": "<destination>",
    "analysis": [
      { "mode": "<mode>", "price": "<price estimate>", "day": "<delivery day estimate>" },
      ...
    ]
  }
  Ensure the output includes only the specified modes and that the response is valid JSON.`;
  
      const payload = {
        contents: [
          {
            parts: [{ text: prompt }],
          },
        ],
        generationConfig: {
          response_mime_type: 'application/json',
          response_schema: cargoResponseSchema,
        },
      };
  
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
  
      if (!response.ok) {
        throw new Error(`Cargo Analysis API Error: ${response.statusText}`);
      }
  
      const data = await response.json();
      const candidate = data.candidates && data.candidates[0];
      const structuredResultText = candidate?.content?.parts?.[0]?.text || '';
  
      try {
        // Attempt to parse the structured JSON result
        const parsed: CargoAnalysisData = JSON.parse(structuredResultText);
  
        // Helper function to process a value if it's a range (e.g., "3000-4500 USD" or "15-25")
        const processRange = (value: string): string => {
          if (value.includes('-')) {
            const parts = value.split('-');
            if (parts.length === 2) {
              // Remove non-numeric characters (allowing decimals) from each part
              const num1 = parseFloat(parts[0].replace(/[^0-9.]/g, ''));
              const num2 = parseFloat(parts[1].replace(/[^0-9.]/g, ''));
              if (!isNaN(num1) && !isNaN(num2)) {
                const avg = Math.round((num1 + num2) / 2);
                
                if (value.toUpperCase().includes('USD')) {
                  return `${avg} USD`;
                }
                return `${avg}`;
              }
            }
          }
          return value.trim();
        };
  
        // Process each analysis item to round price and day values
        parsed.analysis = parsed.analysis.map(item => ({
          ...item,
          price: processRange(item.price),
          day: processRange(item.day),
        }));
  
        setCargoData(parsed);
  
        // Optionally, store the cargo search in Firestore
        const uid = auth.currentUser ? auth.currentUser.uid : 'unknown';
        const email = auth.currentUser ? auth.currentUser.email : 'unknown';
        await addDoc(collection(db, 'cargo_searches'), {
          uid,
          email,
          query: { tons, from: fromLocation, to: toLocation },
          analysis: parsed,
          timestamp: serverTimestamp(),
        });
      } catch (e) {
        // If parsing fails, show the raw text response.
        setResult(structuredResultText);
      }
    } catch (error: any) {
      console.error('Error generating cargo analysis:', error);
      setResult('Error generating cargo analysis.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />

      {/* Top Navigation Bar */}
      <AppBar position="static" color="inherit" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Cargo Management System
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
            <ProfileDropdown />
            <ColorModeSelect sx={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 2000 }} />
          </Box>
        </Toolbar>
      </AppBar>

      <AnalysisContainer direction="column" justifyContent="flex-start" alignItems="center">
        <Container sx={{ position: 'relative', zIndex: 10, mb: 2 }}>
          <Typography
            component="h1"
            variant="h4"
            align="center"
            sx={{ fontSize: 'clamp(2rem, 10vw, 2.15rem)', mb: 3 }}
          >
            Get Cargo Transport Insights
          </Typography>

          {/* Cargo Input Section */}
          <Paper sx={{ p: 3, mb: 2 }} elevation={3}>
            <Box
              component="form"
              autoComplete="off"
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                label="Tons"
                placeholder="Enter number of tons..."
                value={tons}
                onChange={(e) => setTons(e.target.value === '' ? '' : Number(e.target.value))}
              />
              <TextField
                fullWidth
                variant="outlined"
                label="From"
                placeholder="Enter origin location..."
                value={fromLocation}
                onChange={(e) => setFromLocation(e.target.value)}
              />
              <TextField
                fullWidth
                variant="outlined"
                label="To"
                placeholder="Enter destination location..."
                value={toLocation}
                onChange={(e) => setToLocation(e.target.value)}
              />
              {loading ? (
                <Button
                  type="button"
                  fullWidth
                  variant="contained"
                  sx={(theme) => ({
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    pointerEvents: 'none',
                    opacity: 1,
                  })}
                >
                  Processing...
                </Button>
              ) : (
                <Button type="button" fullWidth variant="contained" color="primary" onClick={handleAnalyse}>
                  Get Cargo Details
                </Button>
              )}
            </Box>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress />
              </Box>
            )}
          </Paper>

          {/* Display Cargo Analysis or Raw Result */}
          {cargoData ? (
            <CargoAnalysisResults data={cargoData} />
          ) : result ? (
            <Box
              sx={{
                mt: 3,
                p: 3,
                borderRadius: 2,
                boxShadow: (theme) =>
                  theme.palette.mode === 'dark'
                    ? '0 2px 5px rgba(0,0,0,0.4)'
                    : '0 2px 5px rgba(0,0,0,0.1)',
                backgroundColor: (theme) => theme.palette.background.paper,
              }}
            >
              <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                Analysis Response:
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {result}
              </Typography>
            </Box>
          ) : null}
        </Container>
      </AnalysisContainer>
    </AppTheme>
  );
};

export default AnalysePage;
